import { useState, useMemo } from "react";
import { format } from "date-fns";
import {
  getPaymentDetails,
  getPaymentTypeLabel,
  getRemainingBalanceAfterPayment,
  getCumulativePaidAsOfPayment,
  getPaymentOverdueStatus,
} from "@/utils/paymentHelpers";
import { filterByDateRange, stripUnitPrefix } from "@/utils/salesHelpers";

/**
 * Manages all payments-tab state: search, method/status filters,
 * view tab (list/calendar/tracker), date range,
 * and computes the filtered list + stats + export data.
 *
 * @param {Object} queries - { payments, sales, clients, apartments, receipts }
 */
export function usePaymentFilters({
  payments,
  sales,
  clients,
  apartments,
  receipts,
}) {
  // --- Local state ---
  const [paymentsSearchQuery, setPaymentsSearchQuery] = useState("");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [paymentViewTab, setPaymentViewTab] = useState("list");
  const [paymentsDateRange, setPaymentsDateRange] = useState({
    type: "all",
    startDate: "",
    endDate: "",
  });
  const [isPaymentsFilterOpen, setIsPaymentsFilterOpen] = useState(false);

  // --- Quick-date helper ---
  const setPaymentsQuickDate = (type) => {
    setPaymentsDateRange({ type, startDate: "", endDate: "" });
    setIsPaymentsFilterOpen(false);
  };

  // --- Human-readable label for the date filter button ---
  const getPaymentsFilterLabel = () => {
    switch (paymentsDateRange.type) {
      case "all":
        return "All Time";
      case "thisWeek":
        return "This Week";
      case "lastWeek":
        return "Last Week";
      case "thisMonth":
        return "This Month";
      case "lastMonth":
        return "Last Month";
      case "custom":
        if (paymentsDateRange.startDate && paymentsDateRange.endDate) {
          return `${format(
            new Date(paymentsDateRange.startDate),
            "MMM d"
          )} - ${format(new Date(paymentsDateRange.endDate), "MMM d, yyyy")}`;
        }
        return "Custom Range";
      default:
        return "All Time";
    }
  };

  // --- Computed: filtered payments ---
  const filteredPayments = useMemo(() => {
    return filterByDateRange(payments, "payment_date", paymentsDateRange).filter(
      (payment) => {
        const { client, apartment, sale } = getPaymentDetails(
          payment,
          sales,
          clients,
          apartments,
          receipts
        );
        
        // Whether to show this payment as "overdue" is now based on the
        // sale's current standing (same as the Sales tab), not whether
        // this specific payment was made after its own historical due date.
        const isDelayed = getPaymentOverdueStatus(sale).isDelayed;

        const matchesSearch =
          client?.full_name
            ?.toLowerCase()
            .includes(paymentsSearchQuery.toLowerCase()) ||
          apartment?.unit_number
            ?.toLowerCase()
            .includes(stripUnitPrefix(paymentsSearchQuery).toLowerCase()) ||
          payment.reference_number
            ?.toLowerCase()
            .includes(paymentsSearchQuery.toLowerCase());
            
        const matchesMethod =
          paymentMethodFilter === "all" ||
          payment.payment_method === paymentMethodFilter;
          
        // ✅ Use calculated isDelayed instead of raw payment.is_delayed
        const matchesStatus =
          paymentStatusFilter === "all" ||
          (paymentStatusFilter === "delayed" && isDelayed) ||
          (paymentStatusFilter === "on_time" && !isDelayed);
          
        return matchesSearch && matchesMethod && matchesStatus;
      }
    );
  }, [
    payments,
    paymentsDateRange,
    paymentsSearchQuery,
    paymentMethodFilter,
    paymentStatusFilter,
    sales,
    clients,
    apartments,
    receipts,
  ]);

  // --- Computed: stats ---
  const totalPaymentAmount = useMemo(
    () => payments.reduce((sum, p) => sum + (p.amount || 0), 0),
    [payments]
  );
  
  // A payment counts as "delayed" here based on whether its sale is
  // currently overdue (matches the Sales tab's definition), not whether
  // this specific payment was historically made late.
  const delayedPaymentCount = useMemo(
    () => payments.filter((p) => {
      const { sale } = getPaymentDetails(p, sales, clients, apartments, receipts);
      return getPaymentOverdueStatus(sale).isDelayed;
    }).length,
    [payments, sales, clients, apartments, receipts]
  );
  
  const avgPaymentAmount = useMemo(
    () => (payments.length > 0 ? totalPaymentAmount / payments.length : 0),
    [payments, totalPaymentAmount]
  );

  // --- Computed: export data ---
const paymentsExportData = useMemo(() => {
  return filteredPayments.map((payment) => {
    const { sale, client, apartment, receipts: paymentReceipts } = getPaymentDetails(
      payment,
      sales,
      clients,
      apartments,
      receipts
    );
    const remainingBalance = getRemainingBalanceAfterPayment(
      payment,
      sale,
      payments
    );
    // total_paid must reflect the running balance as of THIS payment, not
    // the sale's current (latest) total — otherwise every payment row for
    // the same sale shows the same final total_paid figure, and it no
    // longer sums with remaining_balance to the sale's total_price.
    const cumulativePaid = sale ? getCumulativePaidAsOfPayment(payment, payments) : 0;

    return {
      payment_date: payment.payment_date,
      client: client?.full_name || "N/A",
      unit: apartment?.unit_number || "N/A",
      contract_number: sale?.contract_number || "-",
      total_price: sale?.total_price || 0,
      currency: payment.currency || "USD",
      payment_method: payment.payment_method,
      payment_type: getPaymentTypeLabel(payment.payment_type),
      total_paid: cumulativePaid,
      remaining_balance: remainingBalance !== null ? remainingBalance : "",
      sale_status: sale?.status || "",
      receipt_status:
        paymentReceipts.length > 0 ? paymentReceipts[0].status : "No Receipt",
      notes: payment.payment_type === "advanced_payment" 
        ? (sale?.notes || payment.notes || "")
        : (payment.notes || ""),
    };
  });
}, [filteredPayments, sales, clients, apartments, receipts, payments]);

  // --- Static: export columns ---
  const paymentsExportColumns = [
    { header: "Date", key: "payment_date" },
    { header: "Client", key: "client" },
    { header: "Unit", key: "unit" },
    { header: "Contract No.", key: "contract_number" },
    { header: "Unit Price", key: "total_price" },
    { header: "Currency", key: "currency" },
    { header: "Payment Method", key: "payment_method" },
    { header: "Payment Type", key: "payment_type" },
    { header: "Total Paid", key: "total_paid" },
    { header: "Remaining Balance", key: "remaining_balance" },
    { header: "Sale Status", key: "sale_status" },
    { header: "Receipt", key: "receipt_status" },
    { header: "Notes", key: "notes" },
  ];

  return {
    // State
    paymentsSearchQuery,
    setPaymentsSearchQuery,
    paymentMethodFilter,
    setPaymentMethodFilter,
    paymentStatusFilter,
    setPaymentStatusFilter,
    paymentViewTab,
    setPaymentViewTab,
    paymentsDateRange,
    setPaymentsDateRange,
    isPaymentsFilterOpen,
    setIsPaymentsFilterOpen,
    // Helpers
    setPaymentsQuickDate,
    getPaymentsFilterLabel,
    // Computed
    filteredPayments,
    totalPaymentAmount,
    delayedPaymentCount,
    avgPaymentAmount,
    // Export
    paymentsExportData,
    paymentsExportColumns,
  };
}